import Tooltip from "../../shared/Tooltips/BasicTooltip";
import { isEmpty } from "lodash";
import { FlakyTest } from "../../shared/type";
import FlakyTestRow, { FlakyRowProps } from "../FlakyTestRow";
import { useState } from "react";
import { useFetch } from "use-http";
import FlakyEmptyState from "./FlakyEmptyState";
import SortingButton from "./SortingButton";

type Props = FlakyRowProps & {
  flakyTests: FlakyTest[];
  tooltips: {
    [K in "flaky_instance" | "first_occurrence_at"]: string;
  };
  query?: string;
  suiteHasRecentRuns: boolean;
  suiteHasPreviousInstances: boolean;
  sortedFlakesEndpoint: string;
  sortBy: SortBy;
  isDescending: boolean;
};

type SortBy = "instance" | "latest_occurrence";

export const ResultButtons: Record<SortBy, string> = {
  instance: "Instances",
  latest_occurrence: "Latest occurrence",
};

const FlakyTestTable = ({
  query,
  suiteHasRecentRuns,
  suiteHasPreviousInstances,
  currentAccountMember,
  isAdmin,
  flakyTestAssignmentFeatureAvailable,
  organizationSettingsUrl,
  teams,
  flakyTests,
  refreshAssignments,
  isFlaky,
  tooltips,
  sortedFlakesEndpoint,
  sortBy,
  isDescending,
}: Props) => {
  if (isEmpty(flakyTests)) {
    return (
      <FlakyEmptyState
        query={query}
        suiteHasRecentRuns={suiteHasRecentRuns}
        suiteHasPreviousInstances={suiteHasPreviousInstances}
      />
    );
  }

  const [sortedFlakes, setSortedFlakes] = useState(flakyTests);
  const [activeButton, setActiveButton] = useState<{
    sortBy: SortBy;
    desc: boolean;
  }>({ sortBy: sortBy, desc: isDescending });
  const fetchOptions = {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  };
  const fetchFlakes = useFetch(fetchOptions);

  const sortFlakes = async (newSortBy) => {
    fetchFlakes.cache.clear();

    const newOrder =
      newSortBy === activeButton.sortBy ? !activeButton.desc : true;

    await fetchFlakes.get(
      `${sortedFlakesEndpoint}sort_by=${newSortBy}&order=${
        newOrder ? "DESC" : "ASC"
      }`,
    );

    if (fetchFlakes.response.ok) {
      setSortedFlakes(fetchFlakes.response.data);
      setActiveButton({ sortBy: newSortBy, desc: newOrder });
    }
  };

  return (
    <section>
      <ul className="ta-panel">
        <li
          aria-hidden={true}
          className="ta-panel--heading grid-cols-[auto_7rem_8rem_8rem] lg:grid-cols-[auto_7rem_14rem_17rem]"
        >
          <div className="main-section">Description</div>
          <div className="flex items-center justify-center">
            {currentAccountMember ? (
              <SortingButton
                updateSort={() => sortFlakes("instance")}
                currentOrder={activeButton.desc}
                currentSort={activeButton.sortBy}
                buttonType="instance"
              />
            ) : (
              ResultButtons["instance"]
            )}

            <Tooltip classNames="ml-1" name="Flaky instances">
              <p className="font-semibold m-0 charcoal-800">Flaky instances</p>
              <p className="m-0 mt-1 charcoal-800">{tooltips.flaky_instance}</p>
            </Tooltip>
          </div>

          <div>
            First occurrence
            <Tooltip classNames="ml-1" name="First occurrence">
              <p className="font-semibold m-0 charcoal-800">First occurrence</p>
              <p className="m-0 mt-1 charcoal-800">
                {tooltips.first_occurrence_at}
              </p>
            </Tooltip>
          </div>

          <div>
            {currentAccountMember ? (
              <SortingButton
                updateSort={() => sortFlakes("latest_occurrence")}
                currentOrder={activeButton.desc}
                currentSort={activeButton.sortBy}
                buttonType="latest_occurrence"
              />
            ) : (
              ResultButtons["latest_occurrence"]
            )}
          </div>
        </li>

        {sortedFlakes.map((flakyTest) => (
          <FlakyTestRow
            key={flakyTest.test_id}
            flakyTest={flakyTest}
            currentAccountMember={currentAccountMember}
            teams={teams}
            isAdmin={isAdmin}
            flakyTestAssignmentFeatureAvailable={
              flakyTestAssignmentFeatureAvailable
            }
            organizationSettingsUrl={organizationSettingsUrl}
            refreshAssignments={refreshAssignments}
            isFlaky={isFlaky}
          />
        ))}
      </ul>
    </section>
  );
};

export default FlakyTestTable;
